import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import ErrorDialog from '../components/ErrorDialog';
import { usePricingPlans } from '../hooks/usePricingPlans';
import { openCashfreeCheckout } from '../lib/cashfree';

// ─── Countdown Timer (Digicrave AnnouncementBar pattern) ──────────────────
function CountdownTimer() {
    const [time, setTime] = useState({ hours: 2, minutes: 45, seconds: 30 });

    useEffect(() => {
        const interval = setInterval(() => {
            setTime(prev => {
                let { hours, minutes, seconds } = prev;
                seconds--;
                if (seconds < 0) { seconds = 59; minutes--; }
                if (minutes < 0) { minutes = 59; hours--; }
                if (hours < 0) { hours = 23; minutes = 59; seconds = 59; }
                return { hours, minutes, seconds };
            });
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const pad = (n) => String(n).padStart(2, '0');

    return (
        <div className="flex items-center gap-3">
            {[{ label: 'HRS', val: time.hours }, { label: 'MIN', val: time.minutes }, { label: 'SEC', val: time.seconds }].map(({ label, val }, i) => (
                <React.Fragment key={label}>
                    {i > 0 && <span className="text-primary font-black text-lg">:</span>}
                    <div className="flex flex-col items-center">
                        <span className="text-xl font-black text-primary tabular-nums">{pad(val)}</span>
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em]">{label}</span>
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
}

// ─── Payment Trust Icons (Digicrave paymentIcons pattern) ──────────────────
function PaymentTrustBar() {
    const methods = ['UPI', 'GPay', 'PhonePe', 'Visa', 'MC', 'RuPay'];
    return (
        <div className="flex items-center gap-1.5 flex-wrap justify-center mt-4">
            {methods.map(name => (
                <span key={name} className="inline-flex items-center justify-center bg-white text-slate-600 text-[10px] font-black px-2 py-1 rounded border border-slate-200 min-w-[36px] h-6 shadow-sm">
                    {name}
                </span>
            ))}
        </div>
    );
}

// ─── Social Proof Row (Digicrave .rating pattern) ──────────────────────────
function SocialProofRow() {
    return (
        <div className="flex items-center justify-center gap-3 mb-8 flex-wrap">
            <div className="flex gap-0.5">
                {'★★★★★'.split('').map((s, i) => (
                    <span key={i} className="text-yellow-400 text-xl">{s}</span>
                ))}
            </div>
            <span className="text-slate-700 font-bold text-base">4.8/5</span>
            <span className="text-slate-400 text-sm">—</span>
            <span className="text-slate-500 text-sm">Trusted by <strong className="text-slate-700">500+ restaurants</strong> across India</span>
        </div>
    );
}

// ─── Main Component ────────────────────────────────────────────────────────

export default function SubscriptionPage() {
    const navigate = useNavigate();
    const { plans: PLANS, accelerators: ACCELERATORS } = usePricingPlans();
    const [loadingId, setLoadingId] = useState(null);
    const [errorDialog, setErrorDialog] = useState({ isOpen: false, title: '', message: '' });
    const [currentPlan, setCurrentPlan] = useState(undefined); // undefined = loading, null = no plan
    const [userInfo, setUserInfo] = useState({ name: '', email: '', phone: '' });
    const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | '3month' | '6month'
    const [showPayment, setShowPayment] = useState(false);
    const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
    const [promoCode, setPromoCode] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [promoError, setPromoError] = useState('');
    const [availableAddons, setAvailableAddons] = useState([]);
    const [selectedAddons, setSelectedAddons] = useState([]);

    const showError = (title, message) => setErrorDialog({ isOpen: true, title, message });

    useEffect(() => {
        const fetchAddons = async () => {
            try {
                const { data } = await supabase.from('plan_addons').select('*').eq('is_active', true).order('sort_order');
                if (data) setAvailableAddons(data);
            } catch (e) {
                console.error('Error fetching addons:', e);
            }
        };
        fetchAddons();
    }, []);

    useEffect(() => {
        const fetchUser = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data: rest } = await supabase.from('restaurants').select('plan, phone, name').eq('owner_id', user.id).single();
            if (rest?.plan) setCurrentPlan(rest.plan);
            else setCurrentPlan(null);
            setUserInfo({ name: rest?.name || user.user_metadata?.name || '', email: user.email || '', phone: rest?.phone || '' });
        };
        fetchUser();
    }, []);

    const currentTier = currentPlan ? ({ starter: 1, growth: 2, pro: 3, enterprise: 4 }[currentPlan] ?? 0) : 0;

    // ─── Cashfree Payment Handler ─────────────────────────────────────────
    const handleSelectPlan = (plan) => {
        if (plan.id === 'enterprise') {
            // Enterprise = contact sales flow
            window.open(`https://wa.me/919999999999?text=Hi%2C%20I%27m%20interested%20in%20the%20Enterprise%20plan%20for%20GetYourQR.%20Restaurant%3A%20${encodeURIComponent(userInfo.name)}`, '_blank');
            return;
        }
        setSelectedPlanDetails(plan);
        setPromoCode('');
        setAppliedPromo(null);
        setPromoError('');
        setSelectedAddons([]);
        setShowPayment(true);
    };

    // Calculate base amount for the currently selected plan
    let baseAmount = 0;
    if (selectedPlanDetails) {
        if (billingCycle !== 'monthly') {
            const acc = ACCELERATORS.find(a => a.id === `${selectedPlanDetails.id}_${billingCycle === '3month' ? '3' : '6'}_month`);
            if (acc) {
                baseAmount = parseFloat(acc.upfront_price.replace('₹', '').replace(',', '').replace('+', ''));
            } else {
                baseAmount = parseFloat(selectedPlanDetails.price.replace('₹', '').replace(',', '').replace('+', ''));
            }
        } else {
            baseAmount = parseFloat(selectedPlanDetails.price.replace('₹', '').replace(',', '').replace('+', ''));
        }
    }

    // Applicable Add-ons for selected plan
    const applicableAddons = availableAddons.filter(a => !a.applicable_plans || a.applicable_plans.length === 0 || a.applicable_plans.includes(selectedPlanDetails?.id));

    // Calculate add-ons cost
    const addonsCost = selectedAddons.reduce((sum, id) => {
        const a = availableAddons.find(addon => addon.id === id);
        return sum + (a ? a.price_value : 0);
    }, 0);

    // Total base amount before discount (Plan + Add-ons)
    const totalBaseAmount = baseAmount + addonsCost;

    // Calculate discount
    let discountAmount = 0;
    if (appliedPromo) {
        if (appliedPromo.discount_type === 'percentage') {
            discountAmount = (appliedPromo.discount_value / 100) * totalBaseAmount;
        } else {
            discountAmount = appliedPromo.discount_value;
        }
        // Cap discount at total amount
        if (discountAmount > totalBaseAmount) discountAmount = totalBaseAmount;
    }
    const finalAmount = Math.max(0, totalBaseAmount - discountAmount);

    const handleApplyPromo = async () => {
        const code = promoCode.trim().toUpperCase();
        if (!code) return;
        
        setIsValidating(true);
        setPromoError('');

        try {
            const { data, error } = await supabase
                .from('plan_coupons')
                .select('*')
                .eq('code', code)
                .eq('is_active', true)
                .single();

            if (error || !data) {
                setPromoError('Invalid coupon code. Please check and try again.');
                return;
            }

            // Check validity period
            const now = new Date();
            if (data.valid_from && new Date(data.valid_from) > now) {
                setPromoError('This coupon is not active yet.');
                return;
            }
            if (data.valid_until && new Date(data.valid_until) < now) {
                setPromoError('This coupon has expired.');
                return;
            }

            // Check usage limit
            if (data.max_uses && data.current_uses >= data.max_uses) {
                setPromoError('This coupon has reached its maximum usage limit.');
                return;
            }

            // Check plan applicability
            if (data.applicable_plans && data.applicable_plans.length > 0) {
                if (!data.applicable_plans.includes(selectedPlanDetails.id)) {
                    setPromoError(`This coupon is not valid for the ${selectedPlanDetails.name} plan.`);
                    return;
                }
            }

            // Check cycle applicability
            if (data.applicable_cycles && data.applicable_cycles.length > 0) {
                if (!data.applicable_cycles.includes(billingCycle)) {
                    setPromoError(`This coupon is not valid for ${billingCycle} billing.`);
                    return;
                }
            }

            // Check minimum price
            if (data.min_plan_price && totalBaseAmount < data.min_plan_price) {
                setPromoError(`Minimum cart value of ₹${data.min_plan_price} required.`);
                return;
            }

            setAppliedPromo(data);
            setPromoError('');
        } catch (err) {
            setPromoError('Error validating coupon. Please try again.');
        } finally {
            setIsValidating(false);
        }
    };

    const handleConfirmPayment = async () => {
        if (!selectedPlanDetails) return;

        // ── Recalculate amount fresh from current state (avoids stale closure bugs) ──
        let currentBase = 0;
        if (billingCycle !== 'monthly') {
            const acc = ACCELERATORS.find(a => a.id === `${selectedPlanDetails.id}_${billingCycle === '3month' ? '3' : '6'}_month`);
            if (acc) {
                currentBase = parseFloat(acc.upfront_price.replace('₹', '').replace(',', '').replace('+', ''));
            } else {
                currentBase = parseFloat(selectedPlanDetails.price.replace('₹', '').replace(',', '').replace('+', ''));
            }
        } else {
            currentBase = parseFloat(selectedPlanDetails.price.replace('₹', '').replace(',', '').replace('+', ''));
        }

        // Add selected add-ons cost
        const currentAddonsCost = selectedAddons.reduce((sum, id) => {
            const a = availableAddons.find(addon => addon.id === id);
            return sum + (a ? (a.price_value || 0) : 0);
        }, 0);
        const currentTotal = currentBase + currentAddonsCost;

        // Apply coupon discount
        let currentDiscount = 0;
        if (appliedPromo) {
            if (appliedPromo.discount_type === 'percentage') {
                currentDiscount = (appliedPromo.discount_value / 100) * currentTotal;
            } else {
                currentDiscount = appliedPromo.discount_value;
            }
            if (currentDiscount > currentTotal) currentDiscount = currentTotal;
        }
        const amountToPay = Math.max(0, currentTotal - currentDiscount);



        setLoadingId(selectedPlanDetails.id);
        setShowPayment(false);

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('You must be logged in to select a plan.');

            let finalPlanName = selectedPlanDetails.name;
            if (billingCycle !== 'monthly') {
                const acc = ACCELERATORS.find(a => a.id === `${selectedPlanDetails.id}_${billingCycle === '3month' ? '3' : '6'}_month`);
                if (acc) finalPlanName = acc.name;
            }

            const couponId = appliedPromo ? appliedPromo.id : 'NONE';
            const addonsPart = selectedAddons.length > 0 ? selectedAddons.join('|') : 'NONE';
            const orderId = `sub_${user.id.slice(0, 8)}_${selectedPlanDetails.id}_${billingCycle}_${couponId}_${addonsPart}_${Date.now()}`;

            // ── FREE PLAN PATH: Coupon makes total ₹0 — activate directly, skip Cashfree ──
            if (amountToPay === 0) {
                const now = new Date();
                const expiresAt = new Date(now);
                let addMonths = 1;
                if (billingCycle === '3month') addMonths = 3;
                if (billingCycle === '6month') addMonths = 6;
                expiresAt.setMonth(expiresAt.getMonth() + addMonths);

                const { data: restData, error: updateErr } = await supabase.from('restaurants').update({
                    plan: selectedPlanDetails.id,
                    cashfree_order_id: orderId,
                    cashfree_payment_id: `free_coupon_${couponId}`,
                    plan_activated_at: now.toISOString(),
                    plan_expires_at: expiresAt.toISOString(),
                }).eq('owner_id', user.id).select('id').single();

                if (updateErr) throw new Error('Failed to activate plan. Please try again.');

                const restaurantId = restData?.id;
                if (restaurantId && addonsPart !== 'NONE') {
                    const addonIds = addonsPart.split('|');
                    const addonInserts = addonIds.map(id => ({
                        restaurant_id: restaurantId,
                        addon_id: id,
                        activated_at: now.toISOString(),
                        expires_at: expiresAt.toISOString(),
                        is_active: true
                    }));
                    await supabase.from('restaurant_addons').delete().eq('restaurant_id', restaurantId).in('addon_id', addonIds);
                    await supabase.from('restaurant_addons').insert(addonInserts);
                }

                if (couponId !== 'NONE') {
                    const { data: cpn } = await supabase.from('plan_coupons').select('current_uses').eq('id', couponId).single();
                    if (cpn) {
                        await supabase.from('plan_coupons').update({ current_uses: (cpn.current_uses || 0) + 1 }).eq('id', couponId);
                    }
                }

                setLoadingId(null);
                // Use hard redirect to ensure ProtectedRoute re-reads the fresh plan from DB
                window.location.href = '/dashboard?activated=true';
                return;
            }

            // ── PAID PLAN PATH: Send to Cashfree ──
            await openCashfreeCheckout({
                amount: amountToPay,
                orderId,
                customerName: userInfo.name,
                customerEmail: userInfo.email,
                customerPhone: userInfo.phone,
                description: `GetYourQR — ${finalPlanName}`,
                paymentType: 'sub',
                onFailure: (err) => {
                    showError('Payment Failed', err.message || 'Could not initiate payment. Please try again.');
                    setLoadingId(null);
                },
            });
        } catch (err) {
            console.error('[SubscriptionPage] Payment error:', err);
            showError('Activation Failed', err.message || 'Could not activate plan. Please try again.');
            setLoadingId(null);
        }
    };

    // ─── Billing Cycle Toggle  ────────────────────────────────────────────
    const billingOptions = [
        { id: 'monthly', label: 'Monthly', sublabel: 'Pay month by month' },
        { id: '3month', label: '3-Month Accelerator', sublabel: 'Save up to 10%', tag: 'SAVE' },
        { id: '6month', label: '6-Month Accelerator', sublabel: 'Save up to 17% · Free month', tag: 'BEST' },
    ];

    // ─── Plan Card Function ────────────────────────────────────────────────────────
    const renderPlanCard = (plan, index) => {
        const tier = { starter: 1, growth: 2, pro: 3, enterprise: 4 }[plan.id];
        const isCurrentPlan = currentPlan === plan.id;
        const isLowerTier = currentTier > tier;
        const isEnterprise = plan.id === 'enterprise';
        const isLoading = loadingId === plan.id;

        // Get accelerator data for this plan
        const accelerator = ACCELERATORS.find(a => a.id === `${plan.id}_${billingCycle === '3month' ? '3' : '6'}_month`);
        const showAsAcc = billingCycle !== 'monthly' && accelerator;

        // Higher index = higher price. We make the box bigger by scaling its vertical padding and pulling it up slightly.
        const sizeClasses = [
          'py-8 mt-6',     // index 0 (Starter)
          'py-10 mt-4',    // index 1 (Growth)
          'py-12 mt-2',    // index 2 (Pro)
          'py-14 mt-0'     // index 3 (Enterprise)
        ][index] || 'py-8 mt-6';

        return (
            <div className={`relative bg-white border rounded-[32px] px-7 flex flex-col h-full transition-colors duration-300 hover:-translate-y-2 shadow-xl shadow-slate-200/50 ${sizeClasses}
                ${plan.is_popular ? 'border-primary ring-2 ring-primary/20 z-10' : 'border-slate-100'}
                ${isLowerTier ? 'opacity-50 pointer-events-none' : ''}`}
            >
                {/* Digicrave .saleBadge */}
                {plan.is_popular && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-[#1A1A1A] font-black text-[10px] uppercase tracking-[0.2em] px-5 py-1.5 rounded-full shadow-sm">
                        🔥 Flash Sale
                    </div>
                )}

                <div className="mb-5">
                    <h2 className="text-xl font-black text-charcoal tracking-tight">{plan.name}</h2>
                    <p className="text-slate-400 text-[11px] font-bold mt-1 min-h-[32px] leading-relaxed">{plan.target_audience}</p>
                </div>

                {/* Digicrave pricing: currentPrice + comparePrice */}
                <div className="mb-6">
                    {showAsAcc ? (
                        <>
                            <div className="flex items-baseline gap-2">
                                <span className="text-5xl font-black text-charcoal tracking-tighter">{accelerator.upfront_price}</span>
                                <span className="text-sm text-slate-400 font-bold uppercase tracking-widest">Total</span>
                            </div>
                            <div className="flex flex-col mt-3 gap-1.5 border-l-2 border-primary/30 pl-3">
                                <div className="text-xs font-black text-green-600 uppercase tracking-widest">{accelerator.savings_text}</div>
                                <div className="text-sm text-slate-500 font-medium">
                                    Effectively <strong className="text-charcoal font-black">{accelerator.effective_monthly}</strong> (vs <span className="line-through">{plan.price}/mo</span>)
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-baseline text-charcoal">
                            <span className="text-5xl font-black tracking-tighter">{plan.price}</span>
                            <span className="text-slate-400 ml-1 font-bold text-sm tracking-widest uppercase">/mo</span>
                        </div>
                    )}
                </div>

                {/* Digicrave dual-button: outline (save) + filled yellow (Buy Now) */}
                <div className="flex flex-col gap-2 mb-6">
                    {/* Primary CTA: Digicrave .buyNow */}
                    <button
                        onClick={() => !isCurrentPlan && !isLowerTier && !isLoading && handleSelectPlan(plan)}
                        disabled={isLoading || isCurrentPlan || isLowerTier}
                        className={`w-full py-4 font-black text-sm uppercase tracking-widest transition-colors flex items-center justify-center gap-2 rounded-2xl
                            ${isCurrentPlan ? 'bg-slate-100 text-slate-400 cursor-not-allowed' :
                              isEnterprise ? 'bg-[#1A1A1A] text-primary hover:bg-slate-800 active:scale-95' :
                              'bg-primary text-[#1A1A1A] hover:bg-[#F0C900] hover:shadow-lg hover:shadow-primary/30 active:scale-95'}
                            ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : isCurrentPlan ? 'Current Plan ✓' : isEnterprise ? 'Contact Sales →' : `${plan.cta_text} →`}
                    </button>


                </div>

                {/* Features */}
                <div className="flex-1 space-y-3">
                    {plan.features.map((feature, i) => (
                        <div key={i} className={`flex items-start gap-2.5 ${feature.included ? 'text-slate-700' : 'text-slate-300'}`}>
                            <span className={`material-symbols-outlined text-lg shrink-0 ${feature.included ? 'text-green-500' : 'text-slate-200'}`}>
                                {feature.included ? 'check_circle' : 'cancel'}
                            </span>
                            <span className={`text-xs leading-relaxed ${feature.included ? 'font-medium' : 'line-through'}`}>{feature.text}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // ─── Main content ─────────────────────────────────────────────────────
    const content = (
        <>
            <div className="max-w-7xl mx-auto">
                <div className="text-center max-w-2xl mx-auto mb-6">
                    <h1 className="text-4xl font-black text-charcoal tracking-tight sm:text-5xl mb-3">
                        Choose Your Plan
                    </h1>
                    <p className="text-slate-500 text-base">
                        Everything you need to run a modern, digital restaurant. Cancel anytime.
                    </p>
                </div>

                {/* Digicrave .rating — Social Proof */}
                <SocialProofRow />

                {/* Digicrave Volume Selector — Billing Cycle Toggle */}
                <div className="flex justify-center mb-16">
                  <div className="bg-white p-2 rounded-full border border-slate-200 flex items-center shadow-sm max-w-full overflow-x-auto">
                    {billingOptions.map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setBillingCycle(opt.id)}
                        className={`px-6 py-3 rounded-full text-sm font-black transition-all whitespace-nowrap flex items-center gap-2 ${
                          billingCycle === opt.id 
                            ? 'bg-primary text-[#1A1A1A] shadow-md' 
                            : 'text-slate-500 hover:text-charcoal hover:bg-slate-50'
                        }`}
                      >
                        {opt.label}
                        {opt.tag && <span className={`text-[10px] px-2 py-0.5 rounded-full ${billingCycle === opt.id ? 'bg-white/50' : 'bg-slate-100 text-slate-600'}`}>{opt.tag}</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Plan Cards Grid */}
                <motion.div layout className={`grid grid-cols-1 md:grid-cols-2 gap-5 items-start max-w-7xl mx-auto mb-10 ${billingCycle !== 'monthly' ? 'lg:max-w-4xl' : 'lg:grid-cols-4'}`}>
                    <AnimatePresence mode="popLayout">
                        {PLANS.filter(p => billingCycle === 'monthly' || (p.id === 'pro' || p.id === 'enterprise'))
                            .map((plan, index) => (
                                <motion.div
                                    key={plan.id}
                                    layout
                                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.8, filter: "blur(4px)" }}
                                    transition={{ duration: 0.4, type: "spring", bounce: 0.2 }}
                                    className="h-full"
                                >
                                    {renderPlanCard(plan, index)}
                                </motion.div>
                            ))}
                    </AnimatePresence>
                </motion.div>

                {/* Digicrave paymentIcons — Trust Bar */}
                <PaymentTrustBar />

                <p className="text-center text-xs text-slate-400 mt-3 font-medium">
                    Secured by Cashfree · 256-bit SSL Encrypted · PCI DSS Compliant
                </p>
            </div>

            {/* ── Confirm Order Modal (Clean Light Mode) ── */}
            {showPayment && selectedPlanDetails && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    <button type="button" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowPayment(false)} />
                    
                    <div className="relative w-full max-w-[420px] bg-white rounded-[32px] p-8 shadow-2xl border border-slate-100 overflow-hidden">
                        
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-8">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center">
                                        <span className="material-symbols-outlined text-slate-800 text-xl">shopping_cart_checkout</span>
                                    </div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">Checkout</h3>
                                </div>
                                <button onClick={() => setShowPayment(false)} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors">✕</button>
                            </div>

                            <div className="bg-[#FDFCF7] p-6 rounded-[24px] border border-primary/20 mb-6">
                                <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">You are upgrading to</div>
                                <div className="text-2xl font-black text-slate-900 flex items-center gap-2">
                                    {billingCycle !== 'monthly' ? ACCELERATORS.find(a => a.id === `${selectedPlanDetails.id}_${billingCycle === '3month' ? '3' : '6'}_month`)?.name : `${selectedPlanDetails.name} Plan`}
                                    <span className="material-symbols-outlined text-primary text-xl">verified</span>
                                </div>
                                
                                <div className="flex items-end gap-2 mt-4">
                                    {/* Show discounted final price if coupon is applied */}
                                    {appliedPromo && discountAmount > 0 ? (
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-baseline gap-2">
                                                <span className="text-5xl font-black text-primary tracking-tighter">
                                                    ₹{finalAmount.toFixed(0)}
                                                </span>
                                                <span className="text-sm text-slate-500 font-bold mb-1.5 tracking-widest">
                                                    {billingCycle !== 'monthly' ? ` / ${billingCycle === '3month' ? '3 months' : '6 months'}` : '/month'}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-slate-400 line-through font-medium">₹{totalBaseAmount.toFixed(0)}</span>
                                                <span className="text-xs font-black text-green-600 bg-green-100 px-2 py-0.5 rounded-full">YOU SAVE ₹{discountAmount.toFixed(0)}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="text-5xl font-black text-primary tracking-tighter">
                                                ₹{totalBaseAmount.toFixed(0)}
                                            </span>
                                            <span className="text-sm text-slate-500 font-bold mb-1.5 tracking-widest">
                                                {billingCycle !== 'monthly' ? ` / ${billingCycle === '3month' ? '3 months' : '6 months'}` : '/month'}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Optional Add-ons */}
                            {applicableAddons.length > 0 && (
                                <div className="mb-6">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">⚡ Supercharge Your Plan</label>
                                    <div className="space-y-2">
                                        {applicableAddons.map(addon => (
                                            <button type="button"
                                                key={addon.id}
                                                onClick={() => {
                                                    if (selectedAddons.includes(addon.id)) {
                                                        setSelectedAddons(selectedAddons.filter(id => id !== addon.id));
                                                    } else {
                                                        setSelectedAddons([...selectedAddons, addon.id]);
                                                    }
                                                }}
                                                className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-colors ${selectedAddons.includes(addon.id) ? 'border-primary bg-primary/5' : 'border-slate-100 hover:border-slate-200'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${selectedAddons.includes(addon.id) ? 'border-primary bg-primary' : 'border-slate-300'}`}>
                                                        {selectedAddons.includes(addon.id) && <span className="material-symbols-outlined text-[14px] text-black">check</span>}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-black text-slate-800">{addon.icon} {addon.name}</div>
                                                        {addon.description && <div className="text-[10px] font-bold text-slate-500 mt-0.5">{addon.description}</div>}
                                                    </div>
                                                </div>
                                                <div className="text-xs font-black text-slate-700">{addon.price}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Plan Coupon Section */}
                            <div className="bg-white rounded-2xl border border-primary/10 p-4 mb-6">
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">🎟️ Have a promo code?</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={promoCode}
                                        onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                                        disabled={!!appliedPromo}
                                        placeholder="ENTER CODE"
                                        className="flex-1 px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-primary disabled:bg-slate-100 uppercase font-bold tracking-widest text-charcoal"
                                    />
                                    {appliedPromo ? (
                                        <button onClick={() => { setAppliedPromo(null); setPromoCode(''); setPromoError(''); }}
                                            className="px-4 py-2.5 text-xs font-bold text-red-500 hover:bg-red-50 rounded-xl transition-colors border border-red-100">
                                            Remove
                                        </button>
                                    ) : (
                                        <button onClick={handleApplyPromo} disabled={!promoCode.trim() || isValidating}
                                            className="px-5 py-2.5 bg-charcoal text-white text-xs font-bold rounded-xl hover:bg-slate-800 transition-colors disabled:opacity-50">
                                            {isValidating ? '...' : 'Apply'}
                                        </button>
                                    )}
                                </div>
                                {promoError && <p className="text-xs text-red-500 mt-2 font-medium">❌ {promoError}</p>}
                                {appliedPromo && (
                                    <div className="mt-3 flex justify-between items-center text-sm font-bold bg-green-50 p-2 rounded-lg border border-green-100">
                                        <span className="text-green-700">✓ Coupon Applied</span>
                                        <span className="text-green-700">- ₹{discountAmount.toFixed(0)}</span>
                                    </div>
                                )}
                            </div>

                            <PaymentTrustBar />

                            <button
                                onClick={handleConfirmPayment}
                                className="w-full bg-primary text-[#1A1A1A] py-4 rounded-2xl text-sm font-black uppercase tracking-widest shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 flex justify-center items-center gap-2 transition-colors hover:scale-[1.02] active:scale-95 mt-4"
                            >
                                <span className="material-symbols-outlined text-lg">lock</span>
                                Pay ₹{finalAmount.toFixed(0)} Securely
                            </button>
                        </div>
                    </div>
                </div>
            )}


            <ErrorDialog isOpen={errorDialog.isOpen} title={errorDialog.title} message={errorDialog.message} onClose={() => setErrorDialog({ ...errorDialog, isOpen: false })} />
        </>
    );

    // Loading state
    if (currentPlan === undefined) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFCF7]">
                <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
        );
    }

    // ⚠️ ALWAYS use StandaloneLayout pattern — subscription page is NEVER inside the sidebar dashboard
    return (
        <div className="min-h-screen bg-[#FDFCF7] relative overflow-hidden font-display">
            <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

            {/* ── Digicrave AnnouncementBar: Flash Offer Countdown ── */}
            <div className="bg-[#1A1A1A] text-white py-3 px-4 relative z-20">
                <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2 text-sm font-black">
                        <span>⚡</span>
                        <span className="uppercase tracking-widest text-primary text-xs">Flash Offer</span>
                        <span className="text-slate-400 text-xs font-medium hidden sm:inline">— Limited-time pricing for early adopters</span>
                    </div>
                    <CountdownTimer />
                    <p className="text-xs text-slate-500 hidden lg:block">No subscription trap — cancel anytime</p>
                </div>
            </div>

            <header className="max-w-7xl mx-auto flex justify-between items-center mb-10 px-6 pt-8 relative z-10">
                <div className="flex items-center gap-3">
                    <img src="/logo.png" alt="GetYourQR Logo" className="w-10 h-10 object-contain rounded-xl bg-charcoal" />
                    <span className="text-xl font-bold tracking-tight uppercase text-[#1A1A1A]">GetYourQR</span>
                </div>
                <button
                    onClick={async () => { await supabase.auth.signOut(); navigate('/login'); }}
                    className="flex items-center gap-2 text-sm font-black text-slate-400 hover:text-red-500 transition-colors uppercase tracking-widest"
                >
                    <span className="material-symbols-outlined text-base">logout</span>
                    Logout
                </button>
            </header>

            <div className="relative z-10 px-4 pb-20">{content}</div>
        </div>
    );
}
